import { useMemo, useState } from 'react';

import { Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { ETaskId } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';

import { useProduct } from '../../api/useProduct';
import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import DialogModal from '../../components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from '../../components/DialogModalManager/constants/modalConfigs';
import EmptyState from '../../components/EmptyState/EmptyState';
import FilterSearchCard from '../../components/Filter/FilterSearchCard';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import ProductFormEditWrapper from '../../components/ProductFormEditWrapper/ProductFormEditWrapper';
import ProductsForm from '../../components/ProductsForm/ProductsForm';
import { permissionsUtil } from '../../utils/permissionsUtil';
import { EToasterStatus, showToast } from '../../utils/showToast';

import '../view.scss';

const dayjsUTC = dayjs.extend(utc);

const ProductsView = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { deleteProduct, addProduct, updateProduct, getProducts } =
    useProduct(publisherId);
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [nameFilter, setNameFilter] = useState('');

  const deleteProductHandler = (productId: string) => {
    deleteProduct.mutate(productId, {
      onSuccess: (data: any) => {
        getProducts.refetch();
        showToast({
          message: `Product has been deleted`,
          status: EToasterStatus.SUCCESS
        });
        setShowDeleteModal(false);
        setSelectedProductId(null);
      },
      onError: (data: any) => {
        showToast({
          message: data.response.data.message,
          status: EToasterStatus.ERROR
        });
        setShowDeleteModal(false);
        setSelectedProductId(null);
      }
    });
  };

  const handleConfirmDelete = () => {
    if (!selectedProductId) return;
    deleteProductHandler(selectedProductId);
  };

  const addProductHandler = (form: any, onMutationComplete: () => void) => {
    addProduct.mutate(form, {
      onSuccess: () => {
        showToast({
          message: `Product has been created`,
          status: EToasterStatus.SUCCESS
        });
        if (hasCategories && isWebStore) {
          setTaskStatus(ETaskId.CREATE_FIRST_PRODUCT);
        }
        navigate('./');
        getProducts.refetch();
        onMutationComplete();
      },
      onError: (data: any) => {
        showToast({
          message: data.response.data.message,
          status: EToasterStatus.ERROR
        });
        onMutationComplete();
      }
    });
  };

  const updateProductHandler = (form: any, onMutationComplete: () => void) => {
    updateProduct.mutate(form, {
      onSuccess: () => {
        showToast({
          message: `Product "${form.data.name}" has been updated`,
          status: EToasterStatus.SUCCESS
        });
        navigate('./');
        getProducts.refetch();
        onMutationComplete();
      },
      onError: (data: any) => {
        showToast({
          message: data.response.data.message,
          status: EToasterStatus.ERROR
        });
        onMutationComplete();
      }
    });
  };

  const handleEditProduct = (productId: string): void => {
    navigate(`./form/${productId}`);
  };

  const handleDuplicateProduct = (productId: string): void => {
    navigate(`./dup/${productId}`);
  };

  const handleDeleteProduct = (productId: string) => {
    if (!productId) return;
    setSelectedProductId(productId);
    setShowDeleteModal(true);
  };

  const COLUMNS: ColumnDef<any>[] = [
    {
      accessorKey: 'image',
      header: 'Image',
      cell: ({ row }: any) => {
        const productImages = row.original.images;
        const productImage = productImages?.find(
          (image: any) => image.type === 'product'
        );
        return productImage ? (
          <img
            src={productImage.url}
            alt={row.original.name}
            className="w-10 h-10 object-cover rounded"
          />
        ) : (
          <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-gray-500 text-xs">No Image</span>
          </div>
        );
      }
    },
    {
      accessorKey: 'name',
      header: 'Product Name',
      cell: ({ getValue }: any) => getValue(),
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.name || '';
        const nameB = rowB.original.name || '';
        return nameA.localeCompare(nameB);
      }
    },
    {
      accessorKey: 'publisherProductId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'createdAt',
      header: 'Created at',
      cell: ({ getValue }: any) =>
        dayjsUTC.utc(getValue()).format('MMM DD, YYYY'),
      sortDescFirst: true,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.createdAt;
        const dateB = rowB.original.createdAt;

        // Handle null/undefined values - put them at the end
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dayjsUTC.utc(dateB).diff(dayjsUTC.utc(dateA));
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }: any) => (
        <ActionCell
          rowData={row.original}
          actions={[
            {
              label: 'Edit',
              handler: () => handleEditProduct(row.original.productId)
            },
            {
              label: 'Duplicate',
              handler: () => handleDuplicateProduct(row.original.productId),
              disabled:
                !permissionsUtil.canUserEdit() ||
                !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.PRODUCTS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            },
            {
              label: 'Delete',
              handler: () => handleDeleteProduct(row.original.productId),
              disabled:
                !permissionsUtil.canUserEdit() ||
                !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.PRODUCTS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            }
          ]}
        />
      )
    }
  ];

  const products = useMemo(() => {
    if (getProducts.isLoading || !getProducts.data) {
      return [];
    }
    return getProducts.data.result;
  }, [getProducts.data, getProducts.isLoading]);

  const filteredProducts = useMemo(() => {
    return [...products].reverse().filter((product) => {
      const lowerName = product.name.toLowerCase();
      return lowerName.includes(nameFilter.toLowerCase());
    });
  }, [products, nameFilter]);

  const BUTTON_TEXT = `${products.length > 0 ? 'Add' : 'Create'} New Product`;

  const renderContent = () => {
    if (getProducts.isLoading || getProducts.isFetching) {
      return (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      );
    }

    if (products.length === 0) {
      return (
        <EmptyState
          title="Add your first Product"
          description="Currently there are no products in your store"
          buttonText={BUTTON_TEXT}
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9">
        <DataTable
          data={filteredProducts}
          columns={COLUMNS}
          rowIdIdentifier="productId"
          bodyDataTestId="dashProductsList"
          rowsPerPage={products.length}
          showPagination={false}
        />
      </div>
    );
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="overflow-y-scroll">
            <PortalHeader
              title="Products"
              primaryButtonText={BUTTON_TEXT}
              onPrimaryButtonClick={() => navigate('./form')}
              primaryButtonState={{
                disabled: !permissionsUtil.canUserEdit(),
                hidden: !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.PRODUCTS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
              }}
              isCreateButton
            />
            {products.length > 0 && (
              <div className="flex px-7 gap-3 mb-5">
                <FilterSearchCard
                  header="Product Name"
                  filterValue={nameFilter}
                  setFilterValue={setNameFilter}
                  isLabelSearchBy
                />
              </div>
            )}
            {renderContent()}
            {showDeleteModal && (
              <DialogModal
                isOpen={showDeleteModal}
                onOpenChange={(open) => {
                  if (!open) setShowDeleteModal(false);
                }}
                title={
                  modalConfigs[PORTAL_MODAL_NAMES.DELETE_PRODUCT_POPUP].title
                }
                description={
                  modalConfigs[PORTAL_MODAL_NAMES.DELETE_PRODUCT_POPUP]
                    .description
                }
                modalActions={{
                  onConfirm: { label: 'Delete', action: handleConfirmDelete },
                  onClose: {
                    label: 'Cancel',
                    action: () => setShowDeleteModal(false),
                    variant: 'link'
                  }
                }}
              />
            )}
          </div>
        }
      />
      <Route
        path="/form"
        element={
          <ProductsForm
            postProduct={addProductHandler}
            headline="New Product"
            buttonText="Save Product"
            data={undefined}
            emptyCache={undefined}
          />
        }
      />
      <Route
        path="/form/:id"
        element={
          <ProductFormEditWrapper
            updateProduct={updateProductHandler}
            postProduct={addProductHandler}
          />
        }
      />
      <Route
        path="/dup/:id"
        element={
          <ProductFormEditWrapper
            updateProduct={updateProductHandler}
            postProduct={addProductHandler}
            dup={true}
          />
        }
      />
    </Routes>
  );
};

export default ProductsView;
